import { Box, Button, Link, Typography, useScreenClass } from '@vp/swan'
import { useLocalizations } from '@vp/ubik-localizations-provider'

import { useCarouselScroll } from '../root/contexts/CarouselScrollContext'
import { useRelatedProducts } from '../root/contexts/RelatedProductsContext'

interface CarouselLinkProps {
  hasColorOptions?: boolean
}

export const CarouselLink: React.FC<CarouselLinkProps> = ({ hasColorOptions }) => {
  const { t } = useLocalizations()
  const screenClass = useScreenClass()
  const { hasRelatedProductsToShow } = useRelatedProducts()
  const {
    scrollTo,
    colorsCarouselInView,
    colorsCarouselMounted,
    colorsHeaderInView,
    colorsHeaderMounted,
    relatedProductsHeaderInView,
    relatedProductsHeaderMounted,
    relatedProductsCarouselInView,
    relatedProductsCarouselMounted,
  } = useCarouselScroll()

  const isMobile = screenClass === 'xs'

  const getLinkText = () => {
    if (hasColorOptions) {
      if (hasRelatedProductsToShow) {
        return t('add-more-colors-or-styles')
      } else {
        return t('color-options.add-more-colors')
      }
    }
    return t('related-product.add-more-styles')
  }

  const handleClick = () => {
    if (hasColorOptions) {
      scrollTo('colorsCarousel', 'center')
    } else {
      scrollTo('relatedProductsCarousel', 'center')
    }
  }

  // Check if related products carousel has mounted and is completely out of view
  const areRelatedProductsOutOfView = () => {
    const areRelatedProductsMounted = relatedProductsHeaderMounted && relatedProductsCarouselMounted
    const areRelatedProductsOutOfView = !relatedProductsHeaderInView && !relatedProductsCarouselInView

    return areRelatedProductsMounted && areRelatedProductsOutOfView
  }

  const showStickyHeader = () => {
    // Colors section
    if (hasColorOptions) {
      // Wait for colors carousel to mount before checking visibility
      // This prevents premature sticky header appearance during initial render
      const areColorsMounted = colorsHeaderMounted && colorsCarouselMounted
      if (!areColorsMounted) return false

      // Check both header and carousel to ensure entire section is out of view
      const areColorsOutOfView = !colorsHeaderInView && !colorsCarouselInView

      // When both color options AND related products exist, we check related products too
      // because they render below colors. This ensures the sticky header only appears
      // after the user scrolls past ALL carousels, not just the first one (colors).
      if (hasRelatedProductsToShow) {
        return areColorsOutOfView && areRelatedProductsOutOfView()
      }

      // If only colors exist (no related products), show sticky header once colors are out of view
      return areColorsOutOfView
    }

    // Related products only section (no color options)
    return areRelatedProductsOutOfView()
  }

  const hasLink = hasRelatedProductsToShow || hasColorOptions

  if (isMobile) {
    return (
      <Box className="quantity-footer-link" mb={hasLink ? 'between-actions' : 0} mt={4}>
        {hasLink && (
          <Link
            render={(p) => (
              <Button skin="link" className={p.className} onClick={handleClick}>
                {p.children}
              </Button>
            )}
          >
            {getLinkText()}
          </Link>
        )}
      </Box>
    )
  }

  return (
    <>
      {showStickyHeader() && (
        <Typography className="qsp-carousel-heading" textAlign="left" fontWeight="bold" py={3}>
          <Link
            render={(p) => (
              <Button skin="link" className={p.className} onClick={handleClick} iconPosition="right">
                {p.children}
              </Button>
            )}
            skin="unstyled"
          >
            {getLinkText()}
          </Link>
        </Typography>
      )}
    </>
  )
}
